import React from "react";
import { useTranslation } from "react-i18next";
import { placeholderLabel } from "../../utility/placeholderLabel";
import DropDown from "components/basic/autoComplete";
import GarageChargerImage from "resources/images/GarageChargerImage.png";
import { useNavigate } from "react-router-dom";
import {
  handleNameChange,
  handlePriceChange,
  handleSelectionChange,
  TextBoxSelection,
  TextBoxSelectionData,
} from "components/basic/textBoxSelection";
import TextArea from "components/basic/textArea";
import { useEditMode } from "../../utility";
import WizardPageLayout from "../../components/WizardPageLayout";
import WizardSection from "../../components/WizardSection";
import WizardButton from "../../components/WizardButton";

const FORM_WIDTH = 440;

interface PaymentsAndAssumptionsProps {
  data: PaymentAndAssumptionsConfigData;
  setData: React.Dispatch<
    React.SetStateAction<PaymentAndAssumptionsConfigData>
  >;
}

export interface PaymentAndAssumptionsConfigData {
  paymentTypes: TextBoxSelectionData[];
  quoteValidFor: string;
  paymentTerms: string;
  assumptions: TextBoxSelectionData[];
}

const quoteValidForOptions = [
  { id: 1, term: "1 Week" },
  { id: 2, term: "2 Weeks" },
  { id: 3, term: "3 Weeks" },
  { id: 4, term: "4 Weeks" },
];

const PaymentAndAssumptions: React.FC<PaymentsAndAssumptionsProps> = ({
  data,
  setData,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const { isEditMode, handleSave, handleCancel } = useEditMode(data, setData);

  return (
    <WizardPageLayout
      title={t("wizard.payments.title")}
      image={{
        src: GarageChargerImage,
        alt: t("wizard.image_alt"),
        side: "left",
      }}
      contentMaxWidth={FORM_WIDTH}
      actions={
        <>
          <WizardButton
            color="primary"
            onClick={() => {
              if (isEditMode) {
                navigate(`?currentPage=${handleCancel()}`);
              } else {
                navigate("?currentPage=installationConfig");
              }
            }}
          >
            {t(isEditMode ? "wizard.cancel" : "wizard.back")}
          </WizardButton>

          <WizardButton
            color="warning"
            onClick={() =>
              isEditMode
                ? navigate(`?currentPage=${handleSave()}`)
                : navigate("?currentPage=reviewAndLaunch")
            }
          >
            {t(isEditMode ? "wizard.save" : "wizard.next")}
          </WizardButton>
        </>
      }
    >
      <WizardSection title={t("wizard.payments.types")}>
        {data.paymentTypes.map((element) => (
          <TextBoxSelection
            key={element.id}
            textBoxSelectionType={element.type}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "paymentTypes",
                element.id,
                element.duplicates,
              )
            }
            onPriceChange={(e) =>
              handlePriceChange(setData, element.id, e, "paymentTypes")
            }
            onNameChange={(e) =>
              handleNameChange(setData, element.id, e, "paymentTypes")
            }
            price={element.price}
            name={element.enteredName}
            placeHolder={placeholderLabel(t, element.placeholder)}
          />
        ))}
      </WizardSection>

      <WizardSection title={t("wizard.payments.valid_for")}>
        <DropDown
          textAlign="center"
          options={quoteValidForOptions.map((item) => ({
            id: item.id,
            label: `${item.term}`,
          }))}
          label={t("wizard.payments.select_weeks")}
          value={
            !data.quoteValidFor
              ? null
              : (quoteValidForOptions.find(
                  (option) => option.term === data.quoteValidFor,
                )?.id ?? 2)
          }
          onChange={(value) => {
            if (!value) {
              return;
            }

            setData((prev) => ({
              ...prev,
              quoteValidFor: value.label,
            }));
          }}
        />
      </WizardSection>

      <WizardSection title={t("wizard.payments.terms")}>
        <TextArea
          placeHolder={t("wizard.payments.terms_placeholder")}
          value={data.paymentTerms}
          onChange={(e) =>
            setData((prev) => ({
              ...prev,
              paymentTerms: e.target.value,
            }))
          }
          minRows={4}
          maxRows={5}
          textAlign="center"
        />
      </WizardSection>

      <WizardSection title={t("wizard.payments.assumptions")}>
        {data.assumptions.map((element) => (
          <TextBoxSelection
            key={element.id}
            textBoxSelectionType={element.type}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "assumptions",
                element.id,
                element.duplicates,
              )
            }
            onPriceChange={(e) =>
              handlePriceChange(setData, element.id, e, "assumptions")
            }
            onNameChange={(e) =>
              handleNameChange(setData, element.id, e, "assumptions")
            }
            price={element.price}
            name={element.enteredName}
            placeHolder={placeholderLabel(t, element.placeholder)}
          />
        ))}
      </WizardSection>
    </WizardPageLayout>
  );
};

export default PaymentAndAssumptions;
